import type { StateCreator } from 'zustand'
import { apiService, type AgentPersona, type TenantAgent } from '@/service/api'
import type { Agent } from '@/components/AgentSelector'
import { loadingState } from './loadingSlice'
import { toast } from 'sonner'
import { buildClientErrorReport } from '@/utils/clientErrorReport'

export interface AgentPersonasState {
  agents: Agent[]
  selectedAgent: Agent | undefined
  selectedAgentForCall: Agent | undefined
  /**
   * True once the user has picked an agent themselves. Distinguishes a real
   * choice from a default we applied, so a refetch can move the default
   * without ever overriding what the user chose.
   */
  selectedAgentExplicit: boolean
  isAgentsLoading: boolean
  fetchAgents: () => Promise<void>
  setSelectedAgent: (agent: Agent) => void
  setSelectedAgentForCall: (agent: Agent | undefined) => void
  getAgentById: (agentId: string) => Agent | undefined
}

const messages = {
  success: {
    fetchAgents: 'Agents loaded successfully',
  },
  error: {
    fetchAgents: 'Failed to load agents',
  },
}

function mapAgentPersonaToAgent(agentPersona: AgentPersona, isAvailable: boolean): Agent {
  return {
    id: agentPersona.id,
    name: agentPersona.name,
    role: agentPersona.role,
    title: agentPersona.title,
    image: agentPersona.image,
    // unavailable agents stay listed but inert — never online,
    // never callable, regardless of what the persona payload claims.
    isOnline: isAvailable ? agentPersona.isOnline : false,
    availableForMeeting: isAvailable ? agentPersona.availableForMeeting : false,
    isAvailable,
  }
}

// Define explicit agent order for display
const AGENT_DISPLAY_ORDER = [
  'plant-support',
  'chemical-agent',
  'mechanical-agent',
  'electrical-agent',
  'epc-agent',
  'maint-agent',
]

// Agents that should always be shown as online (frontend override)
const ALWAYS_ONLINE_AGENTS = [
  'plant-support',
  'chemical-agent',
  'mechanical-agent',  // Max - enabled per INTUAI-1299
  'electrical-agent',
]

function sortAgentsByDisplayOrder(agents: Agent[]): Agent[] {
  return [...agents].sort((a, b) => {
    const indexA = AGENT_DISPLAY_ORDER.indexOf(a.id)
    const indexB = AGENT_DISPLAY_ORDER.indexOf(b.id)
    // Put unknown agents at the end
    const orderA = indexA === -1 ? AGENT_DISPLAY_ORDER.length : indexA
    const orderB = indexB === -1 ? AGENT_DISPLAY_ORDER.length : indexB
    return orderA - orderB
  })
}

function applyOnlineOverrides(agents: Agent[]): Agent[] {
  return agents.map(agent => ({
    ...agent,
    // The always-online override never applies to unavailable agents.
    isOnline: agent.isAvailable && ALWAYS_ONLINE_AGENTS.includes(agent.id) ? true : agent.isOnline,
  }))
}

export const agentPersonasSlice: StateCreator<
  AgentPersonasState & loadingState,
  [],
  [],
  AgentPersonasState
> = (set, get) => ({
  agents: [],
  selectedAgent: undefined,
  selectedAgentForCall: undefined,
  selectedAgentExplicit: false,
  isAgentsLoading: false,

  fetchAgents: async () => {
    const { isAgentsLoading } = get()

    if (isAgentsLoading) {
      return
    }

    try {
      set({ isAgentsLoading: true })

      const [personasResponse, tenantAgentsResponse, preferredAgent] = await Promise.all([
        apiService.getAgentPersonas(),
        apiService.getTenantAgents(),
        // which agent this user's onboarding answers say to open on.
        // Advisory — never gates access, and a failure must not break the list.
        apiService.getPreferredOnboardingAgent().catch(() => ({ agent_id: null })),
      ])

      const enabledAgentIds = new Set(
        tenantAgentsResponse.agents
          .filter((a: TenantAgent) => a.isProvisioned && a.isEnabled)
          .map((a: TenantAgent) => a.id)
      )

      // non-enabled agents stay in the list (the chat grid shows them
      // without the availability dot) but are flagged unavailable so nothing
      // can act on them. Fail-closed posture is unchanged: an API failure
      // still yields an empty list, and availability requires an explicit
      // provisioned+enabled tenant agent.
      const mappedAgents: Agent[] = applyOnlineOverrides(
        sortAgentsByDisplayOrder(
          Object.values(personasResponse.agents)
            .filter(agentPersona => agentPersona.showInFrontend)
            .map(agentPersona =>
              mapAgentPersonaToAgent(agentPersona, enabledAgentIds.has(agentPersona.id))
            )
        )
      )
      // Selection only ever lands on an available agent.
      const availableAgents = mappedAgents.filter(a => a.isAvailable)
      const { selectedAgent, selectedAgentExplicit } = get()
      // absent a real choice, open on the agent the user's onboarding
      // answers point at (a process engineer lands on the Chemical Engineer)
      // rather than on whichever agent happens to sort first. Keyed on
      // `selectedAgentExplicit`, not on "is something selected": a default
      // applied by an earlier fetch must not outrank answers submitted since.
      // Every agent stays selectable regardless.
      const onboardingDefault = preferredAgent.agent_id
        ? availableAgents.find(a => a.id === preferredAgent.agent_id)
        : undefined
      // An explicit choice only survives while it is still available. If it went
      // away we fall back — and drop the flag with it, so the fallback is not
      // mistaken for a choice the user made and newer answers can still move the
      // default.
      const explicitStillAvailable = selectedAgentExplicit && selectedAgent
        ? availableAgents.find(a => a.id === selectedAgent.id)
        : undefined
      const newSelectedAgent = explicitStillAvailable || onboardingDefault || availableAgents[0]

      set({
        agents: mappedAgents,
        selectedAgent: newSelectedAgent,
        selectedAgentExplicit: !!explicitStillAvailable,
        isAgentsLoading: false
      })

    } catch (error) {
      console.error('Failed to fetch agent personas:', error)
      toast.error(messages.error.fetchAgents)
      // Fire-and-forget; the slice state does not type context_id, hence the cast.
      void apiService.reportClientError(
        buildClientErrorReport('agents_load', error, {
          has_context_id: !!(get() as { context_id?: string | null }).context_id,
        })
      )
      set({
        agents: [],
        selectedAgent: undefined,
        isAgentsLoading: false
      })
    }
  },
  setSelectedAgent: (agent: Agent) => {
    set({ selectedAgent: agent, selectedAgentExplicit: true })
  },

  setSelectedAgentForCall: (agent: Agent | undefined) => {
    set({ selectedAgentForCall: agent })
  },
  getAgentById: (agentId: string) => {
    const { agents } = get()
    return agents.find(agent => agent.id === agentId)
  },
})
